import { useEffect, useMemo, useRef } from 'react'
import { useEntitlement } from '../../platform/access/useEntitlement'
import { Async } from '../../platform/async/Async'
import { Skeleton } from '../../platform/async/Skeleton'
import { ButtonLink } from '../../platform/primitives/ButtonLink'
import { useBreakpoint } from '../../platform/responsive/useBreakpoint'
import { usePageMeta } from '../../shell/PageMeta'
import { localToday } from '../../platform/localDate'
import { accountsMeta } from '../accounts/accountsMeta'
import { useAccounts } from '../accounts/useAccounts'
import { useNetWorth } from '../accounts/useNetWorth'
import { readView } from '../cashflow/cashflowPeriod'
import { UpgradePrompt } from '../cashflow/LockedPreview'
import { useCashflowHistory, useCashflowPeriod } from '../cashflow/useCashflow'
import { useInsights } from '../insights/useInsights'
import { useCategories } from '../categories/useCategories'
import { useBudgets } from '../budget/useBudgets'
import { readFilters } from '../transactions/filters'
import { useTransactions } from '../transactions/useTransactions'
import { useHouseholdId } from '../household/useHousehold'
import { useMembers } from '../household/useMembers'
import { currentOf } from '../recurring/monthParam'
import { useMonth } from '../recurring/useRecurring'
import { emitInsightShown, emitInsightsEmpty, emitOverviewEmpty, emitOverviewViewed } from './overviewAnalytics'
import { WidgetGrid } from '../../platform/grid/WidgetGrid'
import { DESKTOP_ORDER, layoutFor, type WidgetId } from './overviewLayout'
import { CONNECT_ACCOUNT_PATH } from './overviewPaths'
import { overviewSubtitle } from './overviewSubtitle'
import { NetWorthWidget } from './widgets/NetWorthWidget'
import { BudgetWidget } from './widgets/BudgetWidget'
import { toBudgetState } from './widgets/budgetView'
import { CashflowWidget } from './widgets/CashflowWidget'
import { toChartState } from './widgets/chartView'
import { RecentTransactionsWidget } from './widgets/RecentTransactionsWidget'
import { toRecentState } from './widgets/recentView'
import { SpentWidget } from './widgets/SpentWidget'
import { toSpentState } from './widgets/spentView'
import { toWhereState } from './widgets/whereView'
import { WhereItWentWidget } from './widgets/WhereItWentWidget'
import { InsightWidget } from './widgets/InsightWidget'
import { toInsightState } from './widgets/insightView'
import { HeatmapWidget } from './widgets/HeatmapWidget'
import { toHeatmapState } from './widgets/heatmapView'
import { useDailySpend } from '../cashflow/useDailySpend'
import { UpcomingWidget } from './widgets/UpcomingWidget'
import { toUpcomingState, upcomingMonth } from './widgets/upcomingView'
import './overview.css'

/**
 * `/overview`. The only file here that calls hooks: it reads each module through
 * that module's exported hook, builds one `AsyncState` per widget and hands it
 * down. The widgets are presentational and never fetch.
 *
 * Accounts decide the page. With none there is nothing to summarise, so one
 * page-level empty stands instead of seven empty widgets (R8); with accounts
 * but no spending, each widget carries its own empty copy. A failure of
 * accounts is a page error because the page cannot tell "no accounts" from
 * "could not load them".
 */
export function Overview() {
  const bp = useBreakpoint()
  // The page reads the accounts list itself (empty, error, analytics, header): a count and a date, no money.
  // `useNetWorth` feeds the hero alone, so dropping the owner-pending net-worth exception deletes the hero
  // and that hook and changes nothing else here.
  const accounts = useAccounts()
  const netWorth = useNetWorth()
  // One day for the whole mount: a render across midnight must not re-key the month query.
  const today = useMemo(() => localToday(), [])
  const entitled = useEntitlement('cashflow_details')
  const insightsEntitled = useEntitlement('spending_insights')
  const tz = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone, [])
  // One call serves Spent and Where it went. The default view is /cashflow's own, so it shares that
  // screen's cache entry and moving between the two is a hit.
  const view = useMemo(() => readView(new URLSearchParams(), today), [today])
  const period = useCashflowPeriod({ view, today, tz, details: entitled })
  const earliest = period.state.status === 'success' ? period.state.data.window.earliest : undefined
  // A free user never fetches history (M3-R16).
  const history = useCashflowHistory({ granularity: 'month', today, tz, enabled: entitled, earliest })
  const spent = useMemo(
    () => toSpentState(period.state, entitled ? history.state : null, today),
    [period.state, history.state, entitled, today],
  )
  const chart = useMemo(() => toChartState(period.state, history.state, today), [period.state, history.state, today])
  const categoriesState = useCategories()
  const categoryNames = useMemo(
    () =>
      new Map(
        categoriesState.status === 'success' || categoriesState.status === 'stale'
          ? categoriesState.data.map((c) => [c.id, c.name] as const)
          : [],
      ),
    [categoriesState],
  )
  const where = useMemo(() => toWhereState(period.state, categoryNames), [period.state, categoryNames])
  // M6's own defaults for "all time, page 1", built from its reader so a new filter field cannot go missing here.
  const recentFilters = useMemo(() => ({ ...readFilters(new URLSearchParams(), new Date()), month: null }), [])
  const recentState = useTransactions(recentFilters)
  const recent = useMemo(() => toRecentState(recentState), [recentState])
  // The heatmap needs the report's currency (the client cannot convert), so it waits for the report.
  const month = today.slice(0, 7)
  const currency = period.state.status === 'success' || period.state.status === 'stale' ? period.state.data.currency : undefined
  const daily = useDailySpend(month, currency)
  const heatmap = useMemo(() => toHeatmapState(period.state, daily), [period.state, daily])
  // Free fetches nothing (the M3-R16 pattern); the server's first insight is shown as served.
  const insightsQ = useInsights({ tz, enabled: insightsEntitled })
  const insight = toInsightState(insightsQ)
  const budgetsState = useBudgets()
  const budgets = useMemo(() => toBudgetState(budgetsState), [budgetsState])
  const monthState = useMonth(currentOf(today))
  const upcoming = useMemo(() => toUpcomingState(monthState, today), [monthState, today])

  const household = useHouseholdId()
  const membersState = useMembers(household.id)
  const memberNames = useMemo(
    () =>
      membersState.status === 'success' || membersState.status === 'stale'
        ? membersState.data.map((m) => m.displayName ?? m.email).filter((n): n is string => n !== null)
        : [],
    [membersState],
  )

  const memberPills = useMemo(
    () =>
      membersState.status === 'success' || membersState.status === 'stale'
        ? membersState.data.flatMap((m) => {
            const name = m.displayName ?? m.email
            return [{ id: m.userId, name: name ?? 'Member' }]
          })
        : [],
    [membersState],
  )

  const ready = accounts.status === 'success' || accounts.status === 'stale'
  const meta = useMemo(() => (ready ? accountsMeta(accounts.data) : null), [ready, accounts])
  const subtitle = meta === null ? null : overviewSubtitle({ members: memberNames, ...meta })
  usePageMeta(subtitle)

  const viewed = useRef(false)
  const emptied = useRef(false)
  const spentEmptied = useRef(false)
  const insightShown = useRef(false)
  useEffect(() => {
    if (ready && !viewed.current) {
      viewed.current = true
      emitOverviewViewed(DESKTOP_ORDER.length)
    }
    if (accounts.status === 'empty' && !emptied.current) {
      emptied.current = true
      emitOverviewEmpty('no_accounts')
    }
    if (ready && spent.status === 'empty' && !spentEmptied.current) {
      spentEmptied.current = true
      emitOverviewEmpty('no_transactions')
    }
    // Never while locked: a free user has no insight to count. Once per mount, whichever state lands first.
    if (insightsEntitled && !insightShown.current) {
      if (insight.status === 'success') {
        insightShown.current = true
        emitInsightShown(insight.data.insight.severity, insight.data.count)
      } else if (insight.status === 'empty') {
        insightShown.current = true
        emitInsightsEmpty()
      }
    }
  }, [ready, accounts.status, spent.status, insightsEntitled, insight])

  if (accounts.status === 'empty' || accounts.status === 'error') {
    return (
      <>
        <Async
          state={accounts}
          surface="accounts"
          skeleton={<Skeleton height={96} />}
          empty={{ title: 'No accounts yet', body: 'Connect an account and your overview fills in.' }}
        >
          {() => null}
        </Async>
        {accounts.status === 'empty' && (
          <ButtonLink to={CONNECT_ACCOUNT_PATH} variant="primary">
            Connect an account
          </ButtonLink>
        )}
      </>
    )
  }

  const renderWidget = (id: WidgetId) => {
    switch (id) {
      case 'net-worth':
        return <NetWorthWidget state={netWorth} />
      case 'upcoming':
        return <UpcomingWidget state={upcoming} month={upcomingMonth(today)} />
      case 'budgets':
        return <BudgetWidget state={budgets} />
      case 'spent':
        return <SpentWidget state={spent} month={upcomingMonth(today)} />
      case 'where-it-went':
        return <WhereItWentWidget state={where} entitled={entitled} />
      case 'recent':
        return <RecentTransactionsWidget state={recent} categories={categoryNames} members={memberPills} />
      case 'cashflow':
        return <CashflowWidget state={chart} entitled={entitled} today={today} />
      case 'heatmap':
        return <HeatmapWidget state={heatmap} month={upcomingMonth(today)} today={today} />
      case 'insight':
        // The page prompt below is the one Upgrade while cash-flow detail is locked; the gate draws its own only when insights alone is.
        return <InsightWidget state={insight} entitled={insightsEntitled} cta={entitled ? 'overlay' : 'none'} />
      default: {
        // Adding a widget id without rendering it is a compile error.
        const unreachable: never = id
        return unreachable
      }
    }
  }

  return (
    <>
      <WidgetGrid items={layoutFor(bp)} render={renderWidget} />
      {/* While cash-flow detail is locked every locked panel uses cta="none", so this is the page's one Upgrade —
          as on /cashflow. It is not a widget, so it sits full width below the grid. */}
      {!entitled && (
        <div className="ov-upgrade">
          <UpgradePrompt />
        </div>
      )}
    </>
  )
}
